/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { motion } from 'motion/react';
import { ArrowRight, Clock3, Music2, UsersRound } from 'lucide-react';
import { PageRoute } from '../types';

interface UserServicesProps {
  setRoute: (route: PageRoute) => void;
}

export default function UserServices({ setRoute }: UserServicesProps) {
  const openSongSubmission = () => {
    setRoute('song-submission');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const openBandRecruitment = () => {
    setRoute('recruit');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div id="services-page" className="bg-[#080A0A] text-white">
      <section className="relative min-h-[420px] md:min-h-[520px] flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_20%,rgba(0,245,212,0.18),transparent_45%),radial-gradient(ellipse_at_15%_80%,rgba(86,70,255,0.16),transparent_40%),linear-gradient(160deg,#0A1010_0%,#080A0A_55%,#0B0F12_100%)]" />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(8,10,10,0.92)_0%,rgba(8,10,10,0.55)_55%,rgba(8,10,10,0.88)_100%)]" />
        <div className="relative z-10 w-full max-w-7xl mx-auto px-5 md:px-8 py-16 md:py-24">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-2xl"
          >
            <span className="inline-block text-xs tracking-[0.28em] font-bold text-[#00F5D4] mb-4">
              USER SERVICES
            </span>
            <h1 className="text-3xl md:text-5xl font-black tracking-tight leading-tight mb-5">
              音楽を、もっと自由に
              <br />
              もっと身近に
            </h1>
            <p className="text-base md:text-lg text-white/60 leading-8">
              楽曲の投稿や各種サービスのご利用など、音楽を楽しむ皆さまに向けたサービスをご案内します。
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="w-full max-w-7xl mx-auto px-5 md:px-8">
          <div className="text-center max-w-2xl mx-auto mb-10 md:mb-12">
            <h2 className="text-2xl md:text-4xl font-black tracking-tight mb-3">
              サービス<span className="bg-gradient-to-r from-[#00F5D4] to-[#00BFA0] bg-clip-text text-transparent">一覧</span>
            </h2>
            <p className="text-sm md:text-base text-white/50 leading-7">
              ご利用目的に合わせて、各サービスの詳細をご確認いただけます。
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            <motion.button
              type="button"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.25 }}
              transition={{ duration: 0.45 }}
              onClick={openSongSubmission}
              className="group text-left rounded-[26px] border border-white/[0.08] bg-gradient-to-br from-[#151A19] to-[#101312] p-7 md:p-8 hover:-translate-y-1 hover:border-[#00F5D4]/35 hover:shadow-[0_18px_50px_rgba(0,245,212,0.12)] transition-all cursor-pointer"
            >
              <div className="w-14 h-14 rounded-xl bg-[#00F5D4]/12 text-[#00F5D4] flex items-center justify-center mb-5">
                <Music2 className="w-7 h-7" />
              </div>
              <p className="text-[11px] font-bold tracking-[0.18em] text-[#00F5D4] mb-2">
                ORIGINAL SONG SUBMISSION
              </p>
              <h3 className="text-xl md:text-2xl font-black mb-3">
                オリジナル楽曲の投稿・伴奏制作
              </h3>
              <p className="text-sm text-white/55 leading-7 mb-6">
                オリジナル楽曲や必要な許諾を得た楽曲を投稿し、伴奏制作を申請できます。楽曲情報や音源ファイルをお送りいただき、制作に向けた確認・検討を行います。
              </p>
              <span className="inline-flex items-center gap-2 text-sm font-bold text-[#00F5D4] group-hover:gap-3 transition-all">
                詳しく見る
                <ArrowRight className="w-4 h-4" />
              </span>
            </motion.button>

                        <motion.button
              type="button"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.25 }}
              transition={{ duration: 0.45, delay: 0.06 }}
              onClick={openBandRecruitment}
              className="group text-left rounded-[26px] border border-white/[0.08] bg-gradient-to-br from-[#151A19] to-[#101312] p-7 md:p-8 hover:-translate-y-1 hover:border-[#00F5D4]/35 hover:shadow-[0_18px_50px_rgba(0,245,212,0.12)] transition-all cursor-pointer"
            >
              <div className="w-14 h-14 rounded-xl bg-[#00F5D4]/12 text-[#00F5D4] flex items-center justify-center mb-5">
                <UsersRound className="w-7 h-7" />
              </div>
              <p className="text-[11px] font-bold tracking-[0.18em] text-[#00F5D4] mb-2">
                ARTIST ONBOARDING
              </p>
              <h3 className="text-xl md:text-2xl font-black mb-3">
                アーティスト登録
              </h3>
              <p className="text-sm text-white/55 leading-7 mb-6">
                アーティストタイプを選択し、SNS情報を提出して登録規約に同意し、Koemoriの音楽エコシステムに参加しましょう。
              </p>
              <span className="inline-flex items-center gap-2 text-sm font-bold text-[#00F5D4] group-hover:gap-3 transition-all">
                登録を始める
                <ArrowRight className="w-4 h-4" />
              </span>
            </motion.button>
<motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.25 }}
              transition={{ duration: 0.45, delay: 0.06 }}
              className="rounded-[26px] border border-dashed border-white/[0.1] bg-white/[0.02] p-7 md:p-8"
            >
              <div className="w-14 h-14 rounded-xl bg-white/[0.05] text-white/35 flex items-center justify-center mb-5">
                <Clock3 className="w-7 h-7" />
              </div>
              <p className="text-[11px] font-bold tracking-[0.18em] text-white/35 mb-2">
                COMING SOON
              </p>
              <h3 className="text-xl md:text-2xl font-black text-white/45 mb-3">
                新しいサービスを準備中
              </h3>
              <p className="text-sm text-white/35 leading-7 mb-6">
                今後、皆さまの音楽体験をより豊かにするサービスを順次追加してまいります。お楽しみに。
              </p>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold text-white/40 bg-white/[0.05]">
                <Clock3 className="w-3.5 h-3.5" />
                近日公開
              </span>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
